// StorageHealthBanner — global strip rendered above the main content for
// system admins when free disk on the data volume is in warning or critical
// territory. Polls /api/admin/storage-health every 60s; non-admins get a
// 403 from the API and render nothing.

import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { DASHBOARD_PATHS } from '@/lib/paths';

const POLL_INTERVAL_MS = 60 * 1000;

interface StorageHealth {
  path: string;
  freeBytes: number | null;
  totalBytes: number | null;
  freeGB: number | null;
  criticalGB: number;
  warningGB: number;
  level: 'ok' | 'warning' | 'critical' | 'unknown';
  retentionWillRun: boolean;
}

export function StorageHealthBanner() {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;
  const [health, setHealth] = useState<StorageHealth | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;

    const fetchHealth = async () => {
      try {
        const data = await api<StorageHealth>(DASHBOARD_PATHS.adminStorageHealth);
        if (!cancelled) setHealth(data);
      } catch {
        if (!cancelled) setHealth(null);
      }
    };

    fetchHealth();
    const id = setInterval(fetchHealth, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [isAdmin]);

  if (!isAdmin || !health) return null;
  if (health.level === 'ok') return null;
  if (dismissed) return null;

  const free = health.freeGB != null ? `${health.freeGB.toFixed(1)} GB` : 'unknown';
  const bg =
    health.level === 'critical' ? 'bg-red-600 text-white'
    : health.level === 'warning' ? 'bg-amber-500 text-black'
    : 'bg-gray-600 text-white';
  const label =
    health.level === 'critical' ? 'Storage critical'
    : health.level === 'warning' ? 'Storage warning'
    : 'Storage unknown';
  const message =
    health.level === 'critical'
      ? `${free} free (≤ ${health.criticalGB} GB) — retention sweeps are auto-pruning historical issue events. Free space or upsize the volume to stop.`
      : health.level === 'warning'
      ? `${free} free (warn ≤ ${health.warningGB} GB, critical ≤ ${health.criticalGB} GB) — clean up or upsize before retention kicks in.`
      : `Disk state unavailable on ${health.path}. Retention is running defensively each day.`;

  return (
    <div
      role="alert"
      className={`flex items-center justify-between gap-3 px-4 py-2 text-sm ${bg}`}
    >
      <div className="flex-1 min-w-0">
        <strong className="mr-2">{label}:</strong>
        <span>{message}</span>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="rounded border border-white/40 px-2 py-0.5 text-xs hover:bg-white/10"
        aria-label="Dismiss storage banner"
        title="Hide until next reload"
      >
        Dismiss
      </button>
    </div>
  );
}
